import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { sendWelcomeEmail, sendVerificationEmail, sendPasswordResetEmail } from '../services/emailService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    return {}
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error && data) {
          setUserProfile(data)
          // Sync user role to localStorage for components that depend on it
          if (data?.role) {
            localStorage.setItem('userRole', data?.role)
          }
        }
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
      // Clear role from localStorage on sign out
      localStorage.removeItem('userRole')
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      // Silently ignore token refresh failures (network offline, etc.)
      if (event === 'TOKEN_REFRESH_FAILED') {
        console.warn('[Auth] Token refresh failed - likely offline. Keeping existing session.');
        setLoading(false);
        return;
      }

      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })?.catch(() => {
      // Silently handle network errors (e.g. "Failed to fetch") during session check
      setLoading(false)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
      
      if (error) {
        return { data, error };
      }
      
      // Check if user is banned
      if (data?.user) {
        const { data: profile, error: profileError } = await supabase?.from('user_profiles')?.select('is_banned')?.eq('id', data?.user?.id)?.single();
        
        if (profileError) {
          // PGRST303 = JWT issued at future (clock skew) — skip ban check and allow login
          if (profileError?.code === 'PGRST303') {
            console.warn('[Auth] Clock skew detected (PGRST303) — skipping ban check, allowing login.');
            return { data, error: null };
          }
          console.error('Error checking ban status:', profileError);
          return { data, error: profileError };
        }
        
        if (profile?.is_banned) {
          // Sign out the user immediately
          await supabase?.auth?.signOut();
          return { 
            data: null, 
            error: { message: 'Hesabınız yönetici tarafından yasaklanmıştır. Lütfen destek ekibiyle iletişime geçin.' } 
          };
        }
      }
      
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata
        }
      })
      
      // Send welcome email after successful signup (non-blocking)
      if (data?.user && !error) {
        // Fire-and-forget: don't await, don't block registration
        sendWelcomeEmail(email, metadata)?.catch(emailError => {
          // Silent fail - log but don't throw
          console.warn('Welcome email failed to send, but registration succeeded:', emailError);
        });
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (profileData) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { error } = await supabase?.from('user_profiles')?.update({
          full_name: profileData?.full_name || profileData?.name,
          bio: profileData?.bio,
          experience_level: profileData?.experience_level || profileData?.experienceLevel,
          phone: profileData?.phone,
          avatar_url: profileData?.avatar_url || profileData?.avatar,
          tc_number: profileData?.tc_number || profileData?.tcNumber,
          birth_date: profileData?.birth_date || profileData?.birthDate,
          emergency_contact_name: profileData?.emergency_contact_name || profileData?.emergencyContactName,
          emergency_contact_phone: profileData?.emergency_contact_phone || profileData?.emergencyContactPhone,
          city: profileData?.city,
          district: profileData?.district,
          address_line: profileData?.address_line || profileData?.addressLine,
          zip_code: profileData?.zip_code || profileData?.zipCode,
          instagram_handle: profileData?.instagram_handle || profileData?.instagramHandle,
          website_url: profileData?.website_url || profileData?.websiteUrl,
          equipment_details: profileData?.equipment_details || profileData?.equipmentDetails,
          weight_kg: profileData?.weight_kg || profileData?.weightKg,
          height_cm: profileData?.height_cm || profileData?.heightCm,
          membership_tier: profileData?.membership_tier,
          has_equipment: profileData?.has_equipment || profileData?.hasEquipment,
          location: profileData?.location
        })?.eq('id', user?.id);
      
      if (!error) {
        // Reload profile from database to get updated values
        await profileOperations?.load(user?.id);
      }
      
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const resendVerificationEmail = async () => {
    if (!user?.email) {
      return { error: { message: 'No email address found' } }
    }

    try {
      // Generate new verification link
      const { data, error } = await supabase?.auth?.resend({
        type: 'signup',
        email: user?.email
      })
      
      // Note: Supabase doesn't return the verification URL directly
      // The verification email is sent automatically by Supabase
      // This is a backup notification via Resend
      if (!error) {
        // Send additional notification via Resend
        const verificationUrl = `${window.location?.origin}/verify-email`;
        await sendVerificationEmail(user?.email, verificationUrl);
      }
      
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const requestPasswordReset = async (email) => {
    try {
      // Step 1: Request password reset from Supabase (primary)
      const { data, error } = await supabase?.auth?.resetPasswordForEmail(email, {
        redirectTo: `${window.location?.origin}/password-reset/update`
      })
      
      // Step 2: Try to send additional notification via Resend (optional, non-blocking)
      if (!error) {
        try {
          const resetUrl = `${window.location?.origin}/password-reset/update`;
          await sendPasswordResetEmail(email, resetUrl);
        } catch (emailError) {
          // Log but don't fail the password reset if custom email fails
          console.warn('Custom email notification failed, but Supabase email was sent:', emailError);
        }
      }
      
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    resendVerificationEmail,
    requestPasswordReset,
    isAuthenticated: !!user,
    isEmailVerified: user?.email_confirmed_at ? true : false
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}